import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "../config/axios";

const SortableMessageItem = ({ message, onDuplicate, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: message.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: "rgba(255, 255, 255, 0.02)",
        borderRadius: 2,
        mb: 1,
      }}
      secondaryAction={
        <Box>
          <IconButton
            edge="end"
            onClick={() => onDuplicate(message)}
            color="primary"
          >
            <CopyIcon />
          </IconButton>
          <IconButton
            edge="end"
            onClick={() => onDelete(message.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          sx={{ cursor: "grab" }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <ListItemText primary={message.command} secondary={message.message} />
      </Box>
    </ListItem>
  );
};

const AutoMessages = () => {
  const [messages, setMessages] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchMessages = async () => {
    try {
      const response = await api.get("/api/messages");
      const autoMessages = response.data
        .filter((msg) => msg.is_auto === 1)
        .sort((a, b) => a.order_index - b.order_index);
      setMessages(autoMessages);
    } catch (error) {
      console.error("Erreur lors de la récupération des messages:", error);
    }
  };

  const persistOrder = async (updatedMessages) => {
    const updates = updatedMessages.map((msg, idx) => ({
      auto_message_id: msg.auto_message_id,
      order_index: idx,
    }));

    try {
      await api.put("/api/auto-messages/reorder", { updates });
      setMessages(
        updatedMessages.map((msg, idx) => ({
          ...msg,
          order_index: idx,
        }))
      );
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'ordre:", error);
      fetchMessages();
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = messages.findIndex((msg) => msg.id === active.id);
    const newIndex = messages.findIndex((msg) => msg.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const updatedMessages = arrayMove(messages, oldIndex, newIndex);
    await persistOrder(updatedMessages);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir supprimer ce message automatique ?"
      )
    ) {
      try {
        const messageToUpdate = messages.find((msg) => msg.id === id);

        await api.put(`/api/messages/${id}`, {
          command: messageToUpdate.command,
          message: messageToUpdate.message,
          category: messageToUpdate.category,
          is_auto: false,
          order_index: 0,
        });
        fetchMessages();
      } catch (error) {
        console.error("Erreur lors de la suppression du message:", error);
      }
    }
  };

  const handleDuplicate = async (message) => {
    try {
      await api.post("/api/auto-messages", {
        message_id: message.id,
        order_index: messages.length,
      });
      fetchMessages();
    } catch (error) {
      console.error("Erreur lors de la duplication du message:", error);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Messages Automatiques
      </Typography>
      <Paper elevation={3} sx={{ p: 2 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={messages.map((msg) => msg.id)}
            strategy={verticalListSortingStrategy}
          >
            <List>
              {messages.map((message, index) => (
                <React.Fragment key={message.id}>
                  <SortableMessageItem
                    message={message}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                  {index < messages.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </Paper>
    </Box>
  );
};

export default AutoMessages;
